/* KANON `ankiety-klienta-2026-09-16` — the Ankiety tab's own surface: the chart strip (direction
 * B's, kept by the owner), and the STREAM of occurrences on the left of the pane.
 *
 * ONE stylesheet for the two because they are one surface in one visual language, which is the same
 * argument `DetailTab.module.css` makes for the three data tabs. Everything this tab shares with
 * those — the card, the range bar's two date fields, the three-column chart grid, the notice — is
 * imported from that file rather than re-declared here; what is below is only what the canon adds.
 *
 * The PANE is `SurveyPane.module.css`, and the way one ANSWER is drawn is
 * `SurveyReading.module.css`, because both are shared with surfaces this file does not own. */

/* ── the two columns ────────────────────────────────────────────────────────── */

/* `align-items: flex-start` so the stream card is exactly as tall as its own ceiling rather than
 * being stretched to the pane's height — which is what makes `position: sticky` inside it mean
 * anything. */
.split {
  display: flex;
  gap: var(--space-4);
  align-items: flex-start;
}

/* A fixed width with `flex: none`, and the card inside carries `min-width: 0`: an explicit width on
 * a flex item caps its MINIMUM, and nowrap content then overflows into the neighbour — a failure
 * this app has paid for once. */
.streamColumn {
  position: sticky;
  top: var(--space-0);
  flex: none;
  width: var(--w-survey-stream);
}

/* ── the stream ─────────────────────────────────────────────────────────────── */

/* It is the `DetailTab` card with its own ceiling. The rows scroll INSIDE it, which is not the
 * double scroll the owner banned on 16 September 2026: that one was a second bar beside the tab's
 * own over the SAME content. This is a two-hundred-row list standing beside a pane read at its own
 * length, and the tab's scroll still belongs to the pane. */
.streamCard {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
  max-block-size: var(--h-survey-stream-max);
  padding: var(--space-15px) var(--space-14px);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
}

.streamHead {
  display: flex;
  flex: none;
  gap: var(--space-2);
  align-items: center;
}

.streamHeading {
  margin: var(--space-0);
  font-size: var(--fs-detail-sec);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-heading);
  color: var(--text);
  letter-spacing: var(--ls-detail-sec);
}

/* How many answers are still waiting to be read.
 *
 * NEUTRAL — owner, 16 September 2026. The ember on this card is the DOT on a row still waiting, and
 * a second ember object four pixels from the heading competed with it for the one thing the accent
 * is spent on here. The badge is a chip like any other; the count is what it says. */
.streamCount {
  display: inline-grid;
  place-items: center;
  min-width: var(--sz-survey-mark);
  height: var(--sz-survey-mark);
  padding: var(--space-0) var(--space-5px);
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-auto);
  color: var(--text);
  background: var(--bg-chip);
  border-radius: var(--radius-sm);
  font-variant-numeric: tabular-nums;
}

/* GEOMETRY ONLY — the treatment is `shared/ui/iconButton.module.css`. The compare control and the
 * way out of compare mode stand in the same slot, so they are the same square. */
.streamAction {
  flex: none;
  width: var(--h-detail-tiny-btn);
  height: var(--h-detail-tiny-btn);
  margin-inline-start: auto;
  background: var(--bg-chip);
  border-radius: var(--radius-mark);
}

/* „Porównujesz: wybierz 2" — what the checkboxes below are for, said in the head they appeared in. */
.streamMode {
  flex: 1;
  margin-inline-start: auto;
  font-size: var(--fs-eyebrow);
  font-style: normal;
  font-weight: var(--fw-bold);
  line-height: var(--lh-normal);
  color: var(--text);
  text-align: end;
}

.streamChips {
  display: flex;
  flex: none;
  flex-wrap: wrap;
  gap: var(--space-5px);
}

/* The form filter. The chosen one is the INVERTED chip the canon draws — ink ground, page ink. Not
 * the accent: a filter position is not an act, and the accent on this tab marks the one control
 * that is. */
.formChip {
  display: inline-flex;
  flex: none;
  align-items: center;
  height: var(--h-survey-chip-tall);
  padding: var(--space-0) var(--space-3);
  font-family: var(--font-body);
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  cursor: pointer;
  background: var(--bg-chip);
  border: none;
  border-radius: var(--r-pill);
}

.formChipOn {
  color: var(--bg);
  background: var(--text);
}

.formChip:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* The rows' own port. `scrollbar-gutter` is deliberately not set: a short history fits, and a
 * reserved gutter under a list of four rows would be a strip of empty card beside every one of
 * them. */
.streamScroller {
  --sb-size: var(--space-2);
  --sb-radius: var(--radius-hair);

  flex: 1;
  min-height: var(--h-survey-stream-min);
  overflow-y: auto;
}

.streamMonth {
  display: flex;
  flex-direction: column;
  gap: var(--space-1px);
}

/* A month over its rows, in sentence case and the quiet ink (owner, 4 November 2026). */
.streamEyebrow {
  padding: var(--space-2) var(--space-3) var(--space-1px);
  font-size: var(--type-label);
  font-style: normal;
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-3);
}

/* One occurrence. A control in both modes, and a paragraph when it was never answered — so the
 * declarations are shared and only the ground and the ink differ. */
.streamRow {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  width: 100%;
  padding: var(--space-2) var(--space-3);
  margin: var(--space-0);
  font-family: var(--font-body);
  text-align: start;
  cursor: pointer;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
}

.streamRow:hover {
  background: var(--bg-inset);
}

/* The row the pane is about. `--bg-chip` and a heavier title — two signals, because a ground alone
 * is a difference a trainer reads as "slightly lighter" rather than as "this one". The heavier
 * title is declared below, after `.streamTitle` itself: a descendant selector outranks it, and
 * stylelint refuses the two in the other order. */
.streamRowOn {
  background: var(--bg-chip);
}

.streamRowOn:hover {
  background: var(--bg-chip);
}

/* A missed occurrence: the fact belongs on the list, the affordance does not. Dimmed through the
 * INK rather than through `opacity`, so the chip beside it keeps its own contrast. */
.streamRowMissed {
  cursor: default;
}

.streamRow:focus-visible {
  outline: var(--border-focus) solid var(--focus);

  /* Flush, not outset: the rows scroll inside a port with `overflow-y: auto`, and an outline drawn
   * outside the row is clipped at the port's own edge on the first and last of them. */
  outline-offset: var(--space-0);
}

/* The checkbox that appears in compare mode. Drawn rather than an `<input>`, because a refused row
 * has to keep `aria-disabled` and stay focusable — a `disabled` input drops out of the tab order
 * and takes the reason attached to it with it. */
.streamBox {
  box-sizing: border-box;
  flex: none;
  width: var(--sz-survey-box);
  height: var(--sz-survey-box);
  background: transparent;
  border: var(--border-ring) solid var(--border-2);
  border-radius: var(--radius-hair);
}

/* A row that cannot be checked still stands in the same column as the ones that can: the box's
 * SPACE is kept while compare mode is on, so a missed week does not jut out to the left of every
 * row above it. */
.streamBoxGap {
  flex: none;
  width: var(--sz-survey-box);
}

.streamRow[aria-checked='true'] .streamBox {
  background: var(--text);
  border-color: var(--text);
}

/* A row of another form, while a comparison is anchored. `aria-disabled` rather than `disabled`, so
 * the reason attached to it stays reachable — and the reason has to be READ, which is why the row
 * is quieter in INK and not behind an `opacity`. A dim took the „po terminie" chip and the date
 * down with the title and made the one row the trainer was looking for the hardest to find. */
.streamRow[aria-disabled='true'] {
  cursor: default;
}

.streamText {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-2px);
  min-width: 0;
}

.streamTitle {
  overflow: hidden;
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  line-height: var(--lh-normal);
  color: var(--text);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.streamWhen {
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
}

/* The three overrides that have to stand after the classes they override — see `.streamRowOn`.
 * Stylelint refuses a descendant selector declared before the class it outranks. */
.streamRowOn .streamTitle {
  font-weight: var(--fw-extrabold);
  color: var(--text);
}

/* A week she did not answer. Quieter than a row that can be opened through WEIGHT — the ink stays
 * readable, because „pominięta · pon 3 sie" is the whole point of drawing the row at all. */
.streamRowMissed .streamTitle {
  font-weight: var(--fw-medium);
  color: var(--text-2);
}

/* A row of another form while a comparison is anchored. Quieter in WEIGHT and one step of ink, not
 * behind an `opacity` — see `.streamRow[aria-disabled='true']`. */
.streamRow[aria-disabled='true'] .streamTitle {
  font-weight: var(--fw-medium);
  color: var(--text-2);
}

/* The read failed, or there is nothing to list. A sentence, not a second `EmptyState`: the drawing
 * and the tone stand in the pane beside this card. */
.streamNote {
  padding: var(--space-4) var(--space-3);
  margin: var(--space-0);
  font-size: var(--fs-caption);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

/* The review dot, and there is only one: solid ember on an occurrence still waiting to be read.
 * „Przejrzana" is the ABSENCE of it — owner, 16 September 2026 — because a grey dot on every row
 * already read marked the majority with something nobody acts on and made the ember one harder to
 * find. The row's own accessible name says what the mark means, which is why the legend went too. */
.dot {
  flex: none;
  width: var(--sz-survey-dot);
  height: var(--sz-survey-dot);
  background: var(--accent);
  border-radius: var(--r-circle);
}

/* ── an occurrence's outcome ────────────────────────────────────────────────── */

/* A row's status as a plain WORD (owner, 4 November 2026): „czeka", „po terminie", „pominięta" —
 * no tinted pill and no dashed one. The box keeps its height so a row is one height either way. */
.statusChip {
  display: inline-flex;
  flex: none;
  align-items: center;
  height: var(--h-survey-chip);
  font-size: var(--type-label);
  font-weight: var(--fw-regular);
  line-height: var(--lh-auto);
  color: var(--text-3);
  white-space: nowrap;
}

/* „po terminie" and „pominięta" are the two words a trainer acts on, so they take the reading ink:
 * the tertiary one measured 3,01:1 on this card in Flame. */
.statusWarn,
.statusMissed {
  color: var(--text-2);
}

/* ── the wait ───────────────────────────────────────────────────────────────── */

.skelStream {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
}

.skelStreamRow {
  width: 100%;
  height: var(--h-survey-stream-row);
}

/* ── „Przypisz ankietę" (8 October 2026) ──────────────────────────────────── */

/* GEOMETRY ONLY — the treatment is `shared/ui/ghostButton`. A 34px pill on the toolbar line. */
.assignBar {
  height: var(--h-control);
  padding: 0 var(--space-14px);
  font-size: var(--type-control);
  font-weight: var(--fw-semibold);
  background: transparent;
  border-radius: var(--r-pill);
}

/* The empty pane: the sentence, and the act it names under it. */
.emptyPane {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-3);
  align-items: center;
  justify-content: center;
}

.assignEmpty {
  height: var(--h-detail-btn);
  padding: 0 var(--space-14px);
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  background: var(--bg-card);
  border-radius: var(--radius-mark);
}

/* ── The toolbar line (4 November 2026) ──────────────────────────────────── */

/* One light line on the tab's ground: what is assigned on the left, the window on the right. It
 * NEVER wraps — a wrap that happened when the names landed would push the charts down — so the
 * names give way with an ellipsis and everything else holds its width. */
.toolbar {
  display: flex;
  flex: none;
  gap: var(--space-4);
  align-items: center;
  justify-content: space-between;
  min-height: var(--h-seg-option);
}


.toolbarSide {
  display: flex;
  flex: none;
  gap: var(--space-10px);
  align-items: center;
  min-width: 0;
}


.toolbarSide:first-child {
  flex: 1 1 auto;
}

.toolbarLabel {
  flex: none;
  font-size: var(--type-control);
  color: var(--text-3);
}

.toolbarAssigned {
  display: flex;
  flex: 0 1 auto;
  align-items: center;
  min-width: 0;
}


.toolbarNames {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}


.toolbarSurvey {
  font-size: var(--type-body);
  font-weight: var(--fw-semibold);
  color: var(--text);
  text-decoration: none;
}

.toolbarSurvey:hover {
  text-decoration: underline;
}

.toolbarSurvey:focus-visible {
  border-radius: var(--r-chip);
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.toolbarMeta {
  font-size: var(--type-control);
  color: var(--text-2);
  white-space: nowrap;
}

.toolbarCadence {
  margin-inline-start: var(--space-10px);
}

.toolbarDivider {
  flex: none;
  width: var(--border-hairline);
  height: 18px;
  background: var(--border-2);
}

.toolbarCount {
  min-width: 96px;
  font-size: var(--type-control);
  color: var(--text-3);
  text-align: end;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.toolbarSkel {
  width: 160px;
  height: var(--space-3);
}

.rangeOption {
  font-size: var(--type-control);
  font-weight: var(--fw-medium);
  font-variant-numeric: tabular-nums;
}

.rangePopup {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
}

.rangePopupTitle {
  margin: var(--space-0);
  font-size: var(--type-control);
  font-weight: var(--fw-semibold);
  color: var(--text);
}

.rangeFields {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  font-size: var(--type-control);
  color: var(--text-2);
}

.rangeField {
  display: inline-flex;
  align-items: center;
  height: var(--h-control);
  padding: var(--space-0) var(--space-3);
  font-weight: var(--fw-medium);
  color: var(--text);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--r-field);
}

.rangeField:focus-within {
  border-color: var(--border-3);
}

.rangeRefusal {
  max-width: 280px;
  margin: var(--space-0);
  font-size: var(--type-label);
  font-weight: var(--fw-semibold);
  color: var(--danger-text-strong);
}
